import { apiRequest } from '../config/api';

export interface ExchangeRate {
  toCurrency: string;
  rate: number;
  source: string;
  lastUpdated: string;
  updatedBy?: string;
}

export interface ExchangeRatesResponse {
  globalRates: ExchangeRate[];
  farmOverrides: ExchangeRate[];
}

export interface FarmCurrencyResponse {
  farmId: string;
  currencyCode: string;
}

export interface FarmCurrencyRateResponse {
  farmId: string;
  currencyCode: string;
  rate: number;
}

class CurrencyService {
  /**
   * Get all exchange rates (global + farm overrides if farmId provided)
   */
  async getExchangeRates(farmId?: string): Promise<ExchangeRatesResponse> {
    const queryParams = farmId ? `?farmId=${farmId}` : '';
    return apiRequest<ExchangeRatesResponse>(`/api/currency/rates${queryParams}`);
  }

  /**
   * Get exchange rate for a specific currency
   */
  async getExchangeRate(toCurrency: string, farmId?: string): Promise<{ toCurrency: string; rate: number }> {
    const queryParams = farmId ? `?farmId=${farmId}` : '';
    return apiRequest<{ toCurrency: string; rate: number }>(`/api/currency/rates/${toCurrency}${queryParams}`);
  }

  /**
   * Create or update global exchange rate (admin only)
   */
  async updateGlobalRate(toCurrency: string, rate: number, source: string = 'MANUAL'): Promise<ExchangeRate> {
    const response = await apiRequest<{ exchangeRate: ExchangeRate }>(
      '/api/currency/rates',
      'POST',
      { toCurrency, rate, source }
    );
    return response.exchangeRate;
  }

  /**
   * Fetch latest rates from API and update (admin only)
   */
  async fetchLatestRates(): Promise<{ updated: Array<{ currency: string; rate: number }>; errors: any[] }> {
    const response = await apiRequest<{ updated: any[]; errors: any[] }>(
      '/api/currency/rates/fetch-latest',
      'POST'
    );
    return response;
  }

  /**
   * Delete farm-specific rate override
   */
  async deleteFarmOverride(toCurrency: string, farmId: string): Promise<void> {
    await apiRequest(`/api/currency/rates/${toCurrency}?farmId=${farmId}`, 'DELETE');
  }

  /**
   * Get farm's currency code
   */
  async getFarmCurrency(farmId: string): Promise<FarmCurrencyResponse> {
    return apiRequest<FarmCurrencyResponse>(`/api/farms/${farmId}/currency`);
  }

  /**
   * Update farm's currency code
   */
  async updateFarmCurrency(farmId: string, currencyCode: string): Promise<FarmCurrencyResponse> {
    const response = await apiRequest<{ farmId: string; currencyCode: string }>(
      `/api/farms/${farmId}/currency`,
      'PATCH',
      { currencyCode }
    );
    return { farmId: response.farmId, currencyCode: response.currencyCode };
  }

  /**
   * Get effective exchange rate for farm
   */
  async getFarmCurrencyRate(farmId: string): Promise<FarmCurrencyRateResponse> {
    return apiRequest<FarmCurrencyRateResponse>(`/api/farms/${farmId}/currency/rate`);
  }

  /**
   * Create or update farm-specific rate override
   */
  async updateFarmRate(farmId: string, toCurrency: string, rate: number, source: string = 'MANUAL'): Promise<ExchangeRate> {
    const response = await apiRequest<{ exchangeRate: ExchangeRate }>(
      `/api/farms/${farmId}/currency/rates/${toCurrency}`,
      'POST',
      { rate, source }
    );
    return response.exchangeRate;
  }

  /**
   * Update existing farm-specific rate override
   */
  async patchFarmRate(farmId: string, toCurrency: string, rate: number, source: string = 'MANUAL'): Promise<ExchangeRate> {
    const response = await apiRequest<{ exchangeRate: ExchangeRate }>(
      `/api/farms/${farmId}/currency/rates/${toCurrency}`,
      'PATCH',
      { rate, source }
    );
    return response.exchangeRate;
  }
}

const currencyService = new CurrencyService();
export default currencyService;







